import React, { useEffect, useState } from 'react';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import CompactPagination from '../../components/billing/CompactPagination';
import { formatDate } from '../../utils/formatters';
import { can } from '../../Utility/Utility';

const defaultFormData = {
  customer_id: '',
  product_id: '',
  freeTicketSupport: 0,
  paidTicketSupport: 0,
  remark: ''
};

const TicketMappingPage = () => {
  const [mappings, setMappings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [effectiveAssignTo, setEffectiveAssignTo] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    customer_id: '',
    product_id: ''
  });
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSubs, setCustomerSubs] = useState([]);
  const [customerSubsLoading, setCustomerSubsLoading] = useState(false);

  useEffect(() => {
    initializePage();
    fetchMappings();
  }, []);

  useEffect(() => {
    if (customers.length > 0 || products.length > 0 || effectiveAssignTo !== '') {
      fetchMappings();
    }
  }, [page, filters.search, filters.customer_id, filters.product_id, effectiveAssignTo]);

  const initializePage = async () => {
    try {
      setLoading(true);
      const sessionRes = await customerService.getSession();
      const userInfo = sessionRes?.success ? sessionRes.userInfo : null;
      const assignToValue = userInfo && userInfo.role !== 'owner' ? userInfo.email : '';
      setEffectiveAssignTo(assignToValue);

      const customerParams = { limit: 5000 };
      if (assignToValue) customerParams.assignTo = assignToValue;
      const [customerRes, productRes] = await Promise.all([
        billingService.getCustomers(customerParams),
        billingService.getProducts()
      ]);

      if (customerRes.success) setCustomers(customerRes.data || []);
      if (productRes.success) setProducts(productRes.data || []);
    } catch (error) {
      console.error('Error fetching ticket mapping data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: filters.search,
        customer_id: filters.customer_id,
        product_id: filters.product_id
      };
      if (effectiveAssignTo) params.assignTo = effectiveAssignTo;

      const mappingRes = await billingService.getTicketMappings(params);

      if (mappingRes.success) {
        setMappings(mappingRes.data || []);
        setTotal(mappingRes.total || 0);
      }
    } catch (error) {
      console.error('Error fetching ticket mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingMapping(null);
    setFormData(defaultFormData);
    setCustomerSearch('');
    setCustomerSubs([]);
  };

  const handleEdit = (mapping) => {
    const cid = mapping.customer_id?._id || mapping.customer_id || '';
    loadCustomerSubscriptions(cid);
    setEditingMapping(mapping);
    setFormData({
      customer_id: cid,
      product_id: mapping.product_id?._id || mapping.product_id || '',
      freeTicketSupport: mapping.freeTicketSupport || 0,
      paidTicketSupport: mapping.paidTicketSupport || 0,
      remark: ''
    });
    setCustomerSearch(
      `${mapping.customerData?.firstname || ''} ${mapping.customerData?.lastname || ''} ${mapping.customerData?.company || ''}`.trim()
    );
    setShowModal(true);
  };

  const handleCustomerSelectInModal = (customerId) => {
    loadCustomerSubscriptions(customerId);
    const existingMapping = mappings.find((m) => {
      const mCid = m.customer_id?._id || m.customer_id;
      return String(mCid) === String(customerId);
    });

    if (existingMapping) {
      setEditingMapping(existingMapping);
      setFormData({
        customer_id: customerId,
        product_id: existingMapping.product_id?._id || existingMapping.product_id || '',
        freeTicketSupport: existingMapping.freeTicketSupport || 0,
        paidTicketSupport: existingMapping.paidTicketSupport || 0,
        remark: ''
      });
    } else {
      setEditingMapping(null);
      setFormData((prev) => ({
        ...prev,
        customer_id: customerId,
        product_id: '',
        freeTicketSupport: 0,
        paidTicketSupport: 0,
        remark: ''
      }));
    }
  };

  const filteredCustomerOptions = customers.filter((customer) => {
    if (!customerSearch.trim()) return true;

    const searchValue = customerSearch.toLowerCase();
    const first = String(customer.firstname || '').toLowerCase();
    const last = String(customer.lastname || '').toLowerCase();
    const company = String(customer.company || '').toLowerCase();
    const phoneValue = String(customer.phone || '').toLowerCase();
    const emailValue = String(customer.email || '').toLowerCase();
    const customerId = String(customer.customerId || customer._id || '').toLowerCase();

    return (
      first.includes(searchValue) ||
      last.includes(searchValue) ||
      company.includes(searchValue) ||
      (first + ' ' + last).includes(searchValue) ||
      (last + ' ' + first).includes(searchValue) ||
      phoneValue.includes(searchValue) ||
      emailValue.includes(searchValue) ||
      customerId.includes(searchValue)
    );
  });

  const safeStr = (v) => (v == null ? '' : String(v));

  const loadCustomerSubscriptions = async (customerId) => {
    if (!customerId) {
      setCustomerSubs([]);
      return;
    }
    try {
      setCustomerSubsLoading(true);
      const pageSize = 500;
      const allSubs = [];
      let page = 1;
      let totalFetched = 0;
      let total = null;
      while (true) {
        const res = await billingService.getSubscriptions({ customer_id: customerId, limit: pageSize, page });
        const batch = Array.isArray(res?.data) ? res.data : [];
        allSubs.push(...batch);
        totalFetched += batch.length;
        if (total == null) total = res?.total ?? 0;
        if (batch.length < pageSize || totalFetched >= total) break;
        page = page + 1;
      }
      setCustomerSubs(allSubs);
    } catch (error) {
      console.error('Error loading customer subscriptions:', error);
      setCustomerSubs([]);
    } finally {
      setCustomerSubsLoading(false);
    }
  };

  const extractProductIdsFromSub = (s) => {
    const ids = new Set();
    if (!s) return ids;
    const push = (v) => {
      if (v == null || v === '') return;
      const str = typeof v === 'string' ? v : v?._id ? String(v._id) : (v?.toHexString ? v.toHexString() : String(v));
      if (str && str !== '') ids.add(str.replace(/^ObjectId\(["']|["']\)$/g, ''));
    };
    push(s.product_id);
    push(s.productId);
    push(s.product?._id);
    push(s.productData?._id);
    push(s.productData?.id);
    push(s.productInfo?._id);
    return ids;
  };

  const extractProductNamesFromSub = (s) => {
    const names = new Set();
    if (!s) return names;
    const push = (v) => { if (v) names.add(String(v).trim().toLowerCase()); };
    push(s.productData?.productName);
    push(s.product?.productName);
    push(s.product_name);
    push(s.productName);
    push(s.custom_alias);
    push(s.productData?.alias);
    return names;
  };

  const getFilteredProductsForCustomer = () => {
    if (!formData.customer_id) return [];
    const subProductIds = new Set();
    const subProductNames = new Set();
    customerSubs.forEach((s) => {
      for (const id of extractProductIdsFromSub(s)) subProductIds.add(id);
      for (const n of extractProductNamesFromSub(s)) subProductNames.add(n);
    });
    const currentProductId = formData.product_id ? String(formData.product_id) : null;
    const hasAnyFilter = subProductIds.size > 0 || subProductNames.size > 0;
    return products.filter((p) => {
      const pid = String(p._id);
      const pname = (p.productName || '').toString().trim().toLowerCase();
      const idHit = subProductIds.has(pid);
      const nameHit = pname && subProductNames.has(pname);
      const currentHit = currentProductId && pid === currentProductId;
      if (currentHit) return true;
      if (hasAnyFilter) return idHit || nameHit;
      // If customer is selected but we couldn't load any subs for scoped user,
      // fall back to full product list to avoid blocking the user.
      return true;
    });
  };

  const listFilterCustomerOptions = customers.filter((customer) => {
    if (!filters.search.trim()) return true;
    const term = filters.search.toLowerCase();
    const fullName = `${safeStr(customer.firstname)} ${safeStr(customer.lastname)}`.toLowerCase();
    return (
      safeStr(customer.firstname).toLowerCase().includes(term) ||
      safeStr(customer.lastname).toLowerCase().includes(term) ||
      safeStr(customer.company).toLowerCase().includes(term) ||
      fullName.includes(term) ||
      safeStr(customer.phone).toLowerCase().includes(term) ||
      safeStr(customer.email).toLowerCase().includes(term) ||
      safeStr(customer.customerId).toLowerCase().includes(term) ||
      String(customer._id).toLowerCase().includes(term)
    );
  });

  const listFilterProductOptions = products.filter((product) => {
    if (!filters.search.trim()) return true;
    const term = filters.search.toLowerCase();
    return (
      safeStr(product.productName).toLowerCase().includes(term) ||
      safeStr(product.description).toLowerCase().includes(term) ||
      safeStr(product.productCode).toLowerCase().includes(term)
    );
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id || !formData.product_id) {
      alert('Please select both customer and product.');
      return;
    }

    if (!formData.remark.trim()) {
      alert('Remark is required to maintain history.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        ...formData,
        freeTicketSupport: parseInt(formData.freeTicketSupport, 10) || 0,
        paidTicketSupport: parseInt(formData.paidTicketSupport, 10) || 0,
        remark: formData.remark.trim()
      };

      const duplicateParams = {
        limit: 10,
        customer_id: payload.customer_id,
        product_id: payload.product_id
      };
      if (effectiveAssignTo) duplicateParams.assignTo = effectiveAssignTo;

      const duplicateRes = await billingService.getTicketMappings(duplicateParams);
      const duplicateMappings = duplicateRes.success ? (duplicateRes.data || []) : [];
      const hasDuplicate = duplicateMappings.some((item) => item._id !== editingMapping?._id);

      if (hasDuplicate) {
        alert('Mapping already exists for this customer and product.');
        setSaving(false);
        return;
      }

      if (editingMapping) {
        await billingService.updateTicketMapping(editingMapping._id, payload);
      } else {
        await billingService.createTicketMapping(payload);
      }

      setShowModal(false);
      resetForm();
      fetchMappings();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Ticket Mapping</h4>
          <p className="text-muted small mb-0">Manage customer and product level free and paid ticket allocation separately from sales.</p>
        </div>
        {can('addTicketMapping') && (
          <button
            className="btn btn-primary shadow-sm px-4"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <i className="bi bi-plus-lg me-2"></i> Add Mapping
          </button>
        )}
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <form className="row g-3" onSubmit={(e) => { e.preventDefault(); fetchMappings(); }}>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control bg-light border-0"
                  placeholder="Search customer by name, email, company..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters((prev) => ({ ...prev, search: e.target.value }));
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select bg-light border-0"
                value={filters.customer_id}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, customer_id: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="">All Customers</option>
                {listFilterCustomerOptions.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {`${safeStr(customer.firstname)} ${safeStr(customer.lastname)}`.trim()}
                    {customer.company ? ` - ${safeStr(customer.company)}` : ''}
                    {customer.phone ? ` [${safeStr(customer.phone)}]` : ''}
                    {customer.email ? ` · ${safeStr(customer.email)}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select bg-light border-0"
                value={filters.product_id}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, product_id: e.target.value }));
                  setPage(1);
                }}
              >
                <option value="">All Products</option>
                {listFilterProductOptions.map((product) => (
                  <option key={product._id} value={product._id}>{product.productName}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-light w-100">Filter</button>
            </div>
            <div className="col-md-2">
              <button
                type="button"
                className="btn btn-light w-100"
                onClick={() => {
                  setFilters({ search: '', customer_id: '', product_id: '' });
                  setPage(1);
                }}
              >
                Clear
              </button>
            </div>
          </form>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Product</th>
                <th>Free Ticket</th>
                <th>Paid Ticket</th>
                <th>Updated</th>
                <th>Last Remark</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">Loading...</td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">No ticket mappings found.</td>
                </tr>
              ) : (
                mappings.map((mapping) => (
                  <tr key={mapping._id}>
                    <td>
                      <div className="fw-600">
                        {mapping.customerData?.firstname} {mapping.customerData?.lastname}
                      </div>
                      <div className="text-xs text-muted">
                        {mapping.customerData?.company || 'Individual'}
                      </div>
                      <div className="text-xs text-muted">
                        {mapping.customerData?.customerId || ''}
                      </div>
                    </td>
                    <td>{mapping.productData?.productName || '-'}</td>
                    <td>{mapping.freeTicketSupport || 0}</td>
                    <td>{mapping.paidTicketSupport || 0}</td>
                    <td>{formatDate(mapping.updated_at || mapping.created_at)}</td>
                    <td>{mapping.remark || '-'}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => handleEdit(mapping)}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {total > limit && (
          <div className="card-footer bg-transparent border-0 py-4 px-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="text-muted small">
                Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} results
              </div>
              <CompactPagination
                page={page}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">{editingMapping ? 'Update Ticket Mapping' : 'Add Ticket Mapping'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                    <label className="form-label text-muted small fw-600">Customer</label>
                    <div className="input-group mb-2">
                      <span className="input-group-text bg-light border-0">
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control bg-light border-0"
                        placeholder="Search customer by name, email, company, phone..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                      />
                    </div>
                    <select
                      className="form-select"
                      value={formData.customer_id}
                      onChange={(e) => {
                        const cid = e.target.value;
                        setFormData({ ...formData, customer_id: cid });
                        if (cid) {
                          handleCustomerSelectInModal(cid);
                        } else {
                          setEditingMapping(null);
                        }
                      }}
                      required
                    >
                      <option value="">Select Customer...</option>
                      {filteredCustomerOptions.map((customer) => (
                        <option key={customer._id} value={customer._id}>
                          {`${safeStr(customer.firstname)} ${safeStr(customer.lastname)}`.trim()}
                          {customer.company ? ` - ${safeStr(customer.company)}` : ''}
                          {customer.phone ? ` [${safeStr(customer.phone)}]` : ''}
                          {customer.email ? ` · ${safeStr(customer.email)}` : ''}
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-muted mt-1">
                      {formData.customer_id ? (
                        editingMapping ? (
                          <span className="text-info fw-600">
                            <i className="bi bi-arrow-repeat me-1"></i>
                            Existing mapping found — auto-loaded for update
                          </span>
                        ) : (
                          <span className="text-success fw-600">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            Customer selected
                          </span>
                        )
                      ) : (
                        filteredCustomerOptions.length === 0 ? (
                          <span className="text-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>No customers match your search.
                          </span>
                        ) : (
                          <span><i className="bi bi-info-circle me-1"></i>Search or select a customer to begin ({filteredCustomerOptions.length} available).</span>
                        )
                      )}
                    </div>
                  </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Product</label>
                      <select
                        className="form-select"
                        value={formData.product_id}
                        onChange={(e) => setFormData({ ...formData, product_id: e.target.value })}
                        disabled={!formData.customer_id || customerSubsLoading}
                        required
                      >
                        <option value="">
                          {!formData.customer_id
                            ? 'Select a customer first...'
                            : customerSubsLoading
                              ? 'Loading products...'
                              : 'Select Product...'}
                        </option>
                        {formData.customer_id && !customerSubsLoading && getFilteredProductsForCustomer().length === 0 && (
                          <option value="" disabled>
                            {` — No sales/subscriptions found for this customer — `}
                          </option>
                        )}
                        {formData.customer_id && !customerSubsLoading && getFilteredProductsForCustomer().map((product) => (
                          <option key={product._id} value={product._id}>{product.productName}</option>
                        ))}
                      </select>
                      {formData.customer_id && !customerSubsLoading && (
                        <div className="text-xs text-muted mt-1">
                          {(customerSubs.length === 0 && getFilteredProductsForCustomer().length === products.length) ? (
                            <span className="text-warning">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Showing all products (couldn't locate sales for scoped view).
                            </span>
                          ) : (
                            <span>
                              <i className="bi bi-check2-circle text-success me-1"></i>
                              Showing {getFilteredProductsForCustomer().length} product{getFilteredProductsForCustomer().length === 1 ? '' : 's'} with existing sales for this customer
                              ({customerSubs.length} subscription{customerSubs.length === 1 ? '' : 's'} found).
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Free Ticket Support</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={formData.freeTicketSupport}
                        onChange={(e) => setFormData({ ...formData, freeTicketSupport: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label text-muted small fw-600">Paid Ticket Support</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        value={formData.paidTicketSupport}
                        onChange={(e) => setFormData({ ...formData, paidTicketSupport: e.target.value })}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label text-muted small fw-600">Remark</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.remark}
                        onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                        placeholder="Enter update remark"
                        required
                      ></textarea>
                    </div>
                  </div>

                  {editingMapping?.history?.length > 0 && (
                    <div className="mt-4">
                      <h6 className="fw-700 mb-3">Update History</h6>
                      <div className="border rounded-3 bg-light p-3" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                        {editingMapping.history.slice().reverse().map((item, index) => (
                          <div key={index} className="mb-3 pb-3 border-bottom last-child-no-border">
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-xs fw-700">{item.updated_by || '-'}</span>
                              <span className="text-xs text-muted">{formatDate(item.updated_at)}</span>
                            </div>
                            <div className="text-xs text-muted mb-1">
                              Free Ticket: {item.old_value?.freeTicketSupport ?? '-'} to {item.new_value?.freeTicketSupport ?? '-'}
                            </div>
                            <div className="text-xs text-muted mb-1">
                              Paid Ticket: {item.old_value?.paidTicketSupport ?? '-'} to {item.new_value?.paidTicketSupport ?? '-'}
                            </div>
                            <div className="text-xs text-dark">{item.remark || '-'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0 p-4">
                  <button type="button" className="btn btn-light px-4" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                    {saving ? 'Saving...' : (editingMapping ? 'Update Mapping' : 'Save Mapping')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketMappingPage;
